// TODO: switch to @wandb/weave/components/RadixTooltip once it exposes Arrow.
import * as Tooltip from '@radix-ui/react-tooltip';
import {Icon, IconButton} from '@wandb/components';
import {Tailwind, TailwindContents} from '@wandb/weave/components/Tailwind';
import {clsx} from 'clsx';
import React, {useEffect, useState} from 'react';
import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from 'react-aria-components';

import {isMountedInShell} from '../../shellRemote';
import type {UnseenSettledThread} from '../chat/threadReadStatus';
import {AGENT_NAME} from './constants';

export const WBAgentChatButton = ({
  disableTooltip,
  isLoading,
  showIntroTooltip,
  hideIntroTooltip,
  unseenSettledThread,
  onImpression,
  onPress,
  ...props
}: AriaButtonProps & {
  disableTooltip?: boolean;
  isLoading?: boolean;
  showIntroTooltip?: boolean;
  hideIntroTooltip: () => void;
  unseenSettledThread: UnseenSettledThread | null;
  onImpression?: () => void;
}) => {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const isAriaShellHosted = isMountedInShell();
  // Forge hosts more than W&B, so the tooltip names what that surface calls
  // things: experiments rather than runs, and Forge rather than W&B.
  const tooltipSummary = isAriaShellHosted
    ? 'Analyze and visualize experiments, write code, launch training, or ask any Forge question'
    : 'Analyze and visualize runs, write code, launch experiments, or ask any W&B question';

  const dismissTooltip = () => {
    hideIntroTooltip();
    setIsTooltipOpen(false);
  };
  const handlePress: AriaButtonProps['onPress'] = event => {
    dismissTooltip();
    onPress?.(event);
  };

  // Fire once on mount — the button only renders when visible (i.e. the agent
  // is enabled for the user).
  useEffect(() => {
    onImpression?.();
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const button = (
    <AriaButton
      aria-label={isLoading ? `${AGENT_NAME} (loading)` : AGENT_NAME}
      {...props}
      onPress={handlePress}
      className="group relative rounded-full p-4 mr-8">
      {showIntroTooltip && (
        <span
          aria-hidden
          className="pointer-events-none absolute inset-4 rounded-[5px] bg-[color-mix(in_srgb,var(--wb-accent-bg)_50%,transparent)] blur-[8px] dark:blur-[8px] motion-safe:animate-pulse motion-reduce:opacity-60"
        />
      )}
      <div
        className={clsx(
          'relative inline-flex h-32 pl-12 pr-14 items-center gap-8 rounded-[5px] text-base font-semibold whitespace-nowrap outline outline-2 outline-transparent group-focus-visible:outline-wb-accent group-focus-visible:rounded-[9px] group-focus-visible:outline-offset-2 group-aria-[expanded=true]:border-wb-accent group-aria-[expanded=true]:bg-[color-mix(in_srgb,var(--wb-accent)_14%,transparent)]',
          isAriaShellHosted
            ? 'border border-wb-border bg-transparent text-wb-text-primary group-hover:border-wb-border-strong group-hover:bg-wb-surface-hover'
            : 'border border-moon-550 bg-white/[0.06] text-white group-hover:border-moon-450 group-hover:bg-white/[0.11]',
          "font-['Source_Sans_3',sans-serif]"
        )}>
        <span
          className={
            isLoading
              ? 'inline-flex animate-spin [animation-duration:4s]'
              : 'inline-flex text-wb-accent'
          }>
          <Icon
            icon={isLoading ? 'loading' : 'lightboard-solid'}
            size="md"
            color={
              isLoading
                ? isAriaShellHosted
                  ? 'moon-500'
                  : 'moon-50'
                : 'currentcolor'
            }
          />
        </span>
        Ask {AGENT_NAME}
      </div>
      {unseenSettledThread != null && (
        <div
          role="img"
          aria-label="Unread notice"
          className="absolute -right-2 -top-5">
          <Icon icon="circle-filled" size="xs" color="red-500" />
        </div>
      )}
    </AriaButton>
  );

  return (
    <Tailwind>
      {disableTooltip ? (
        button
      ) : (
        <Tooltip.Provider delayDuration={0}>
          <Tooltip.Root
            open={showIntroTooltip || isTooltipOpen}
            onOpenChange={setIsTooltipOpen}>
            <Tooltip.Trigger asChild>{button}</Tooltip.Trigger>
            <Tooltip.Portal>
              <TooltipContent
                side="bottom"
                sideOffset={8}
                className="relative z-[999999] max-w-[300px] rounded text-sm leading-[140%] shadow-md bg-wb-surface-inverse p-12 text-wb-text-inverse">
                <div>
                  <div className="font-semibold text-wb-text-inverse">
                    {AGENT_NAME}: a coding agent for the AI loop
                  </div>
                  {showIntroTooltip && (
                    <div className="absolute right-8 top-8">
                      <IconButton
                        aria-label={`Dismiss ${AGENT_NAME} tooltip`}
                        icon="close"
                        size="sm"
                        variant="ghost"
                        onPress={dismissTooltip}
                      />
                    </div>
                  )}
                </div>
                <div className="mt-4 text-wb-text-disabled">
                  {tooltipSummary}
                </div>
                <Tooltip.Arrow className="fill-wb-surface-inverse" />
              </TooltipContent>
            </Tooltip.Portal>
          </Tooltip.Root>
        </Tooltip.Provider>
      )}
    </Tailwind>
  );
};

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof Tooltip.Content>,
  React.ComponentPropsWithoutRef<typeof Tooltip.Content>
>((props, ref) => (
  <TailwindContents>
    <Tooltip.Content ref={ref} {...props} />
  </TailwindContents>
));
